import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { useResolvePath } from "./hooks/useResolvePath";
import { CMSPageChrome } from "./components/CMSPageChrome";
import { CMSPageSEO } from "./components/CMSPageSEO";
import { ComposableSection } from "../adminNext/screens/content-sections/renderer/ComposableSection";
import { PageConfigProvider } from "../adminNext/screens/content-sections/renderer/PageConfigContext";
import type { PageConfig } from "../adminNext/screens/content-sections/editor/types";

export function CMSPageResolver() {
  const { pathname } = useLocation();
  const { data, isLoading, isError } = useResolvePath(pathname);

  const sortedSections = useMemo(() => {
    if (!data?.sections) return [];
    return [...data.sections].sort((a, b) => a.display_order - b.display_order);
  }, [data?.sections]);

  if (isLoading) {
    return (
      <Center h="100vh" bg="charcoal.charcoal">
        <Spinner size="xl" color="orange.400" />
      </Center>
    );
  }

  if (isError || !data) {
    return (
      <Center h="100vh" bg="charcoal.charcoal" gc-testing-id="cmsPageNotFound">
        <Box textAlign="center">
          <Text fontSize="4xl" fontWeight="bold" color="white">
            404
          </Text>
          <Text color="whiteAlpha.700" mt={2}>
            Page not found
          </Text>
        </Box>
      </Center>
    );
  }

  const pageConfig = (data.page_config as unknown as PageConfig) ?? null;

  return (
    <Box gc-testing-id="cmsPageResolver" w="100%">
      <CMSPageSEO
        seo={pageConfig?.seo ?? null}
        socialSharing={pageConfig?.social_sharing ?? null}
        pathname={pathname}
      />
      <CMSPageChrome pageConfig={pageConfig}>
        <PageConfigProvider config={pageConfig}>
        {sortedSections.map((section) => {
          if (section.source_type === "composable") {
            return (
              <ComposableSection
                key={section.id}
                sectionId={section.id}
                body={section.body ?? ""}
              />
            );
          }
          return (
            <Box
              key={section.id}
              p={4}
              color="whiteAlpha.600"
              gc-testing-id={`cmsLegacySection-${section.id}`}
            >
              Legacy section: {section.source_type}
            </Box>
          );
        })}
        </PageConfigProvider>
      </CMSPageChrome>
    </Box>
  );
}
